import { useEffect, useRef } from 'react';
import './AmbientCanvas.css';

type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  hue: number;
  alpha: number;
};

interface AmbientCanvasProps {
  className?: string;
  density?: number;
  animated?: boolean;
}

export default function AmbientCanvas({ className = '', density = 64, animated = true }: AmbientCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const pointerRef = useRef({ x: -9999, y: -9999 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const shouldAnimate = animated && !reduced;
    let raf = 0;
    let width = 0;
    let height = 0;
    let dpr = 1;
    let particles: Particle[] = [];

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.offsetWidth || window.innerWidth;
      height = canvas.offsetHeight || window.innerHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = shouldAnimate ? density : Math.max(12, Math.floor(density / 3));
      particles = Array.from({ length: count }, (_, i) => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.28,
        vy: (Math.random() - 0.5) * 0.24,
        r: 0.7 + Math.random() * 2.4,
        hue: i % 3 === 0 ? 192 : i % 3 === 1 ? 252 : 216,
        alpha: 0.18 + Math.random() * 0.42,
      }));
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      const g1 = ctx.createRadialGradient(width * 0.18, height * 0.12, 0, width * 0.18, height * 0.12, Math.max(width, height) * 0.62);
      g1.addColorStop(0, 'rgba(71, 151, 255, 0.18)');
      g1.addColorStop(0.46, 'rgba(119, 91, 255, 0.07)');
      g1.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = g1;
      ctx.fillRect(0, 0, width, height);

      const g2 = ctx.createRadialGradient(width * 0.86, height * 0.82, 0, width * 0.86, height * 0.82, Math.max(width, height) * 0.58);
      g2.addColorStop(0, 'rgba(18, 214, 217, 0.13)');
      g2.addColorStop(0.52, 'rgba(130, 86, 255, 0.06)');
      g2.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = g2;
      ctx.fillRect(0, 0, width, height);

      for (const p of particles) {
        if (shouldAnimate) {
          p.x += p.vx;
          p.y += p.vy;
          if (p.x < -20) p.x = width + 20;
          if (p.x > width + 20) p.x = -20;
          if (p.y < -20) p.y = height + 20;
          if (p.y > height + 20) p.y = -20;
        }
        const dx = pointerRef.current.x - p.x;
        const dy = pointerRef.current.y - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const boost = dist < 180 ? (1 - dist / 180) * 0.38 : 0;
        ctx.beginPath();
        ctx.fillStyle = `hsla(${p.hue}, 95%, 72%, ${Math.min(0.78, p.alpha + boost)})`;
        ctx.shadowColor = `hsla(${p.hue}, 95%, 65%, ${0.32 + boost})`;
        ctx.shadowBlur = 18 + boost * 42;
        ctx.arc(p.x, p.y, p.r + boost * 1.8, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.shadowBlur = 0;
      if (shouldAnimate) raf = requestAnimationFrame(draw);
    };

    const onPointer = (event: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      pointerRef.current = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    };
    const onLeave = () => { pointerRef.current = { x: -9999, y: -9999 }; };

    resize();
    draw();
    window.addEventListener('resize', resize);
    window.addEventListener('pointermove', onPointer, { passive: true });
    window.addEventListener('pointerleave', onLeave);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', onPointer);
      window.removeEventListener('pointerleave', onLeave);
    };
  }, [density, animated]);

  return <canvas ref={canvasRef} className={`ambient-canvas ${className}`} aria-hidden="true" />;
}
